import { useCallback, useEffect, useState } from "react";
import { C } from "../lib/theme.js";
import { api } from "../lib/api.js";

export default function LoginGate({ children }) {
  const [status, setStatus] = useState("checking"); // checking | signed-out | signed-in
  const [demo, setDemo] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((r) => {
        if (cancelled) return;
        setStatus(r?.authenticated ? "signed-in" : "signed-out");
        setDemo(Boolean(r?.demo));
      })
      .catch(() => {
        if (!cancelled) setStatus("signed-out");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (e) => {
      e.preventDefault();
      if (!password) return;
      setBusy(true);
      setError(null);
      try {
        await api.login(password);
        setStatus("signed-in");
        setPassword("");
      } catch (err) {
        setError(err.message || "Sign-in failed");
      } finally {
        setBusy(false);
      }
    },
    [password]
  );

  if (status === "checking") {
    return <CenterMsg>Checking session…</CenterMsg>;
  }

  if (status === "signed-out") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: C.bg,
          fontFamily: "'Inter',sans-serif",
          padding: 20,
        }}
      >
        <form
          onSubmit={signIn}
          style={{
            width: "100%",
            maxWidth: 360,
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: 28,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: C.accent,
              fontFamily: "'DM Mono',monospace",
              marginBottom: 6,
            }}
          >
            Rental Dashboard
          </div>
          <div
            style={{
              fontSize: 24,
              fontFamily: "'Instrument Serif',serif",
              color: C.text,
              marginBottom: 4,
            }}
          >
            Sign in
          </div>
          <div style={{ fontSize: 12, color: C.sub, marginBottom: 16 }}>
            Enter the shared access password.
          </div>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              fontSize: 14,
              marginBottom: 12,
              boxSizing: "border-box",
            }}
          />
          {error && (
            <div
              style={{
                fontSize: 12,
                color: C.red,
                background: C.redBg,
                border: `1px solid ${C.red}40`,
                borderRadius: 5,
                padding: "6px 10px",
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy || !password}
            style={{
              width: "100%",
              background: C.accent,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "10px 12px",
              fontSize: 13,
              fontWeight: 600,
              cursor: busy || !password ? "not-allowed" : "pointer",
              opacity: busy || !password ? 0.6 : 1,
            }}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <>
      <SignOutBar onSignOut={() => setStatus("signed-out")} demo={demo} />
      {typeof children === "function" ? children({ demo }) : children}
    </>
  );
}

function SignOutBar({ onSignOut, demo }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      await api.logout();
    } catch {
      // best-effort
    }
    onSignOut();
  };
  return (
    <div
      style={{
        position: "fixed",
        top: 8,
        right: 12,
        zIndex: 100,
        display: "flex",
        gap: 6,
        alignItems: "center",
      }}
    >
      {demo && (
        <span
          style={{
            fontSize: 10,
            fontFamily: "'DM Mono',monospace",
            color: C.accent,
            background: C.accentLight,
            padding: "3px 7px",
            borderRadius: 4,
            fontWeight: 600,
          }}
        >
          Investor View
        </span>
      )}
      <button
        onClick={click}
        disabled={busy}
        style={{
          background: "transparent",
          border: `1px solid ${C.border}`,
          color: C.sub,
          fontSize: 11,
          padding: "3px 8px",
          borderRadius: 4,
          cursor: busy ? "wait" : "pointer",
          fontFamily: "'DM Mono',monospace",
        }}
        title="Sign out"
      >
        Sign out
      </button>
    </div>
  );
}

function CenterMsg({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.bg,
        color: C.sub,
        fontSize: 13,
        fontFamily: "'Inter',sans-serif",
      }}
    >
      {children}
    </div>
  );
}
